import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-hot-toast';

// Use environment variable or fallback to relative URL
const baseURL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 60000, // 60 seconds for complex searches
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - unwrap backend envelope + handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Auto-unwrap the backend's standard success envelope:
    //   { success: true, data: X, ...rest } → response.data = X
    // This is a no-op for controllers that respond with raw data (vehicle, vcdb, etc.)
    const body = response.data;
    if (
      body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      body.success === true &&
      Object.prototype.hasOwnProperty.call(body, 'data')
    ) {
      return { ...response, data: body.data };
    }
    return response;
  },
  (error: AxiosError<{ message?: string }>) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      toast.error('Session expired. Please login again.');
    }

    // Handle other errors
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    toast.error(errorMessage);

    return Promise.reject(error);
  }
);

export default apiClient;
